// ChatTranscript — the scrollable message-list shell.
//
// .claude/research/design/02: the full CONVERSATION transcript is what expands into the
// right zone's Conversation slot (it is the HISTORY role — distinct from the
// composer intake and the in-document delivery). This is that transcript's
// CONTENTS: a scroll region hosting a list of entries, each rendered by
// ChatMessage per kind. It owns NO entry kind itself — it maps entries to
// ChatMessage and forwards the proposal pointer intents, so adding an entry kind
// touches only ChatMessage's match, never this shell (module-topology: a generic
// host over a per-kind renderer).
//
// DENSITY (chat-ux.md §3): `full` streams every entry as its full artifact.
// `compact` is a different reading contract — only the CURRENT exchange stays
// legible (rendered at compact density: dim author line, clamped agent answer,
// one-line proposal chip) and everything before it collapses to a keyboard-
// operable "N earlier" breadcrumb the reader expands on demand. The split of
// "what is recent" is the pure `splitTranscriptForCompact` (presentation.ts), so
// the renderer and its unit test agree; the breadcrumb's open/closed is
// view-local UI state (like the tail-pin below), not app state.
//
// A trailing DRAFTING status is an optional slot (`drafting`): while a turn is
// in flight the transcript shows the chat-side DraftingIndicator as its last
// item, resolving the golden-path arc (author message → drafting → proposal
// card) in one view. It composes DraftingIndicator; the streaming demo that
// drives it lives in story-level state, never here.
//
// The list is a `role="log"` `aria-live="polite"` region (the modern agent-chat
// convention) so appended entries are announced without a focus steal. Composes
// ScrollArea + ChatMessage + DraftingIndicator. Props-driven: `entries`
// (+ optional `drafting`) in; the proposal intents report UP.
//
// TAIL PINNING (§4 R6): a transcript shows the NEWEST exchange — the viewport
// is scrolled to the bottom on mount and on every append, so the author always
// sees the message they just sent. The one exception: an author who scrolled
// UP to read history is never yanked back down; pinning resumes once they
// scroll near the bottom again. This is DOM scroll geometry (measure +
// position), a view-local concern every presentation (compact/panel/full)
// must share — it lives here, not in each wrapper.

import { exhaustSwitchCase } from "@bilda/utils";
import { Plural, useLingui } from "@lingui/react/macro";
import {
    Fragment,
    type ReactNode,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import { useNow } from "../../../hooks/useNow.js";
import { AiMarker } from "../../AiMarker/AiMarker.js";
import { Icon } from "../../Icon/Icon.js";
import type { FocusTarget, ResourceRef } from "../../Navigation/types.js";
import { ScrollArea } from "../../ScrollArea/ScrollArea.js";
import { ChatMessage } from "../ChatMessage/ChatMessage.js";
import { DraftingIndicator } from "../DraftingIndicator/DraftingIndicator.js";
import type { ResolveInlineLink } from "../InlineLink/InlineLink.js";
import {
    assistantTurnHeads,
    type ChatDensity,
    entryKey,
    splitTranscriptForCompact,
    trimDanglingColonPreambles,
} from "../presentation.js";
import {
    canSendAgain,
    isWaiting,
    projectThread,
    type ThreadItem,
    threadItemEntries,
    turnMadeSomething,
} from "../thread.js";
import type {
    ChatEntry,
    ChatFieldAnswer,
    DraftingPhase,
    FormSourceOption,
    InterruptionAction,
} from "../types.js";
import {
    DayDivider,
    NewSinceDivider,
    TrailRun,
    WaitingPill,
} from "./ThreadRows.js";
import "./ChatTranscript.css";

export type ChatTranscriptProps = {
    readonly entries: readonly ChatEntry[];
    // The reading contract — roomy (`full`, default) or dense (`compact`).
    readonly density?: ChatDensity;
    // Optional trailing status: the agent is drafting, or a run resolved.
    readonly drafting?: DraftingPhase;
    // Proposal pointer intents — forwarded to each proposal entry.
    readonly onGoToChange?: (id: string) => void;
    // A proposal card's Accept / Discard (by the proposal's id).
    readonly onAcceptProposal?: (proposalId: string) => void;
    readonly onDiscardProposal?: (proposalId: string) => void;
    readonly onReviewInPlace?: (id: string) => void;
    // Open a created material as a tab — forwarded to each material entry.
    readonly onOpenMaterial?: (materialId: string) => void;
    // Open/download an exported package — forwarded to each export entry.
    readonly onOpenExport?: (exportId: string) => void;
    readonly onDownloadExport?: (exportId: string) => void;
    // Turn-epilogue intents (LANE S3) — forwarded to the form / action-offer
    // entries. `onSubmitForm` / `onDismissForm` answer an ElicitationForm;
    // `onSendPrompt` re-drives the agent for an action-offer send_prompt move.
    readonly onSubmitForm?: (
        requestId: string,
        answers: readonly ChatFieldAnswer[],
    ) => void;
    readonly onDismissForm?: (requestId: string) => void;
    // The library's sources a `field_sources` form question offers (D42).
    readonly formSources?: readonly FormSourceOption[];
    readonly onSendPrompt?: (prompt: string) => void;
    // Open the shell's file picker for one Source (F9/F15's `request_upload`
    // move) — forwarded to each action-offer entry.
    readonly onRequestUpload?: (
        sourceId: string,
        sourceTitle: string | undefined,
    ) => void;
    // Open a cited source (cite-B) — forwarded to each agent entry's citations.
    readonly onOpenSource?: (resourceId: string) => void;
    readonly onOpenResource?: (ref: ResourceRef, target?: FocusTarget) => void;
    // Agent markdown links → resource chips (see InlineLink).
    readonly resolveLink?: ResolveInlineLink;
    // The interruption notice's recovery — wired ONLY to the transcript's
    // tail, and only while no turn is in flight, so a notice in scrollback
    // never offers a stale action.
    readonly onInterruptionAction?: (action: InterruptionAction) => void;
    // The first entry you have not seen before ("New since you were here"),
    // fixed when the conversation opened.
    readonly newSinceId?: string;
    // The assistant's questions wait in the composer's place (full density,
    // pen "Chat – every entry" §4): a pending one leaves the thread, a
    // skipped one (`skippedQuestions`, view state of the conversation) is a
    // line with "Answer now".
    readonly questionsInComposer?: boolean;
    readonly skippedQuestions?: ReadonlySet<string>;
    readonly onAnswerNow?: (requestId: string) => void;
    // Names the log landmark for AT (caller-owned / localised).
    readonly ariaLabel: string;
    // Caps the scroll height in the story/host; the transcript scrolls beyond.
    readonly maxHeight?: string | number;
    readonly className?: string;
};

// An author within this many px of the bottom still counts as pinned, so
// sub-pixel rounding and momentum-scroll overshoot never break the anchor.
const PIN_THRESHOLD_PX = 32;

export const ChatTranscript = ({
    entries: rawEntries,
    density = "full",
    drafting,
    onGoToChange,
    onAcceptProposal,
    onDiscardProposal,
    onReviewInPlace,
    onOpenMaterial,
    onOpenExport,
    onDownloadExport,
    onSubmitForm,
    onDismissForm,
    formSources,
    onSendPrompt,
    onRequestUpload,
    onOpenSource,
    onOpenResource,
    resolveLink,
    onInterruptionAction,
    newSinceId,
    questionsInComposer = false,
    skippedQuestions = new Set<string>(),
    onAnswerNow,
    ariaLabel,
    maxHeight,
    className,
}: ChatTranscriptProps) => {
    // The dangling-colon rendering guard (Dm8) runs once, here, ahead of every
    // other read of `entries` (compact split, tail-pin, per-kind render) — a
    // pure cosmetic edit to the SAME entry ids, so nothing downstream needs to
    // know it ran.
    const inComposer = questionsInComposer && density === "full";
    const entries = trimDanglingColonPreambles(rawEntries).filter(
        (entry) =>
            !(
                inComposer &&
                entry.type === "information_request" &&
                entry.status.type === "pending" &&
                !skippedQuestions.has(entry.id)
            ),
    );
    const viewportRef = useRef<HTMLDivElement | null>(null);
    // Whether the author is at (or near) the tail; scrolling up unpins.
    const pinnedRef = useRef(true);
    // Compact only: whether the "N earlier" breadcrumb is expanded. View-local.
    const [showEarlier, setShowEarlier] = useState(false);
    // How many cards waiting for you are scrolled out of sight (DOM geometry,
    // view-local; drives the "N waiting for you" pill).
    const [waitingOutOfView, setWaitingOutOfView] = useState(0);
    const waitingCount = entries.filter(isWaiting).length;
    const now = useNow(60_000);

    // biome-ignore lint/correctness/useExhaustiveDependencies: `waitingCount` re-measures when a card starts or stops waiting; the body reads only the DOM.
    useEffect(() => {
        const viewport = viewportRef.current;
        if (viewport === null) return undefined;
        const measureWaiting = () => {
            const view = viewport.getBoundingClientRect();
            setWaitingOutOfView(
                [...viewport.querySelectorAll("[data-waiting]")].filter(
                    (el) => {
                        const box = el.getBoundingClientRect();
                        return box.bottom < view.top || box.top > view.bottom;
                    },
                ).length,
            );
        };
        const onScroll = () => {
            pinnedRef.current =
                viewport.scrollHeight -
                    viewport.scrollTop -
                    viewport.clientHeight <
                PIN_THRESHOLD_PX;
            measureWaiting();
        };
        measureWaiting();
        viewport.addEventListener("scroll", onScroll);
        return () => viewport.removeEventListener("scroll", onScroll);
    }, [waitingCount]);
    const showWaiting = () =>
        viewportRef.current
            ?.querySelector("[data-waiting]")
            ?.scrollIntoView({ block: "center", behavior: "smooth" });

    // Pin on mount and on every append (new tail entry or drafting change).
    // The author's OWN message re-pins even after scrolling up: sending is an
    // explicit "show me the exchange" intent; only agent-side appends respect
    // an unpinned reading position.
    const tail = entries.at(-1);
    const tailId = tail?.id;
    const tailIsAuthor = tail?.type === "author";
    // biome-ignore lint/correctness/useExhaustiveDependencies: tailId/drafting ARE the append signal; the effect body reads only refs and the derived flag.
    useLayoutEffect(() => {
        const viewport = viewportRef.current;
        if (viewport === null) return;
        if (tailIsAuthor) pinnedRef.current = true;
        if (pinnedRef.current) {
            viewport.scrollTop = viewport.scrollHeight;
        }
    }, [tailId, drafting]);

    // While a turn is in flight, a running call belongs to the live row (D51),
    // so activity trails list only finished steps.
    const inFlight = drafting?.type === "drafting";
    // The React key is entry-kind-QUALIFIED, not the bare `entry.id`. Two
    // distinct entries can legitimately share one `messageId`: when a turn ends
    // by raising a form AND the agent wrote preamble prose, sendTurn commits the
    // closing `AgentMessageSent` and the `RequestInformation` marker under the
    // SAME `agentMessageId` (F121) — so an `agent` bubble and an
    // `information_request` card project with the same id and would otherwise
    // collide on one key. Qualifying by `type` keeps the key total and unique
    // across kinds without touching entry identity: `entry.id` still drives the
    // `inFlight` tail match below and the F115 log-sequence ordering upstream.
    // Full density names the speaker once per assistant turn (the panel's
    // "Assistant · AI" head); compact keeps its dense contract.
    const heads =
        density === "full" ? assistantTurnHeads(entries) : new Set<string>();
    const renderEntry = (entry: ChatEntry) => (
        <Fragment key={entryKey(entry)}>
            {heads.has(entryKey(entry)) ? <AssistantTurnHead /> : null}
            {renderMessage(entry)}
        </Fragment>
    );
    const renderMessage = (entry: ChatEntry) => {
        const message = renderChatMessage(entry);
        return isWaiting(entry) ? (
            <div data-waiting="true">{message}</div>
        ) : (
            message
        );
    };
    const live = (id: string) => id === tailId && !inFlight;
    const renderChatMessage = (entry: ChatEntry, inTrailRun = false) => (
        <ChatMessage
            entry={entry}
            density={density}
            inTrailRun={inTrailRun}
            onGoToChange={onGoToChange}
            onAcceptProposal={onAcceptProposal}
            onDiscardProposal={onDiscardProposal}
            onReviewInPlace={onReviewInPlace}
            onOpenMaterial={onOpenMaterial}
            onOpenExport={onOpenExport}
            onDownloadExport={onDownloadExport}
            onSubmitForm={onSubmitForm}
            onDismissForm={onDismissForm}
            formSources={formSources}
            onSendPrompt={onSendPrompt}
            onRequestUpload={onRequestUpload}
            onOpenSource={onOpenSource}
            onOpenResource={onOpenResource}
            resolveLink={resolveLink}
            inFlight={inFlight}
            onInterruptionAction={
                live(entry.id) ? onInterruptionAction : undefined
            }
            noticeContext={{
                turnMadeSomething: turnMadeSomething(entries, entry.id),
                live: live(entry.id),
            }}
            questionsInComposer={inComposer}
            onAnswerNow={
                skippedQuestions.has(entry.id) && onAnswerNow !== undefined
                    ? () => onAnswerNow(entry.id)
                    : undefined
            }
        />
    );
    // Full density: the thread as the reader sees it (thread.ts) — merged
    // trail lines, a not-sent message as one row, a divider per day.
    const renderItem = (item: ThreadItem): ReactNode => {
        const first = threadItemEntries(item)[0];
        const head =
            first !== undefined && heads.has(entryKey(first)) ? (
                <AssistantTurnHead />
            ) : null;
        switch (item.type) {
            case "entry":
                return (
                    <Fragment key={item.key}>
                        {head}
                        {renderMessage(item.entry)}
                    </Fragment>
                );
            case "unsent_message": {
                const retry =
                    live(item.noticeId) &&
                    canSendAgain(item.cause) &&
                    onInterruptionAction !== undefined
                        ? () => onInterruptionAction("try_again")
                        : undefined;
                return (
                    <ChatMessage
                        key={item.key}
                        entry={item.entry}
                        resolveLink={resolveLink}
                        onOpenResource={onOpenResource}
                        unsent={
                            retry === undefined ? {} : { onTryAgain: retry }
                        }
                    />
                );
            }
            case "trail_run":
                return (
                    <Fragment key={item.key}>
                        {head}
                        <TrailRun
                            entries={item.entries}
                            inFlight={inFlight}
                            renderEntry={(entry) => (
                                <Fragment key={entryKey(entry)}>
                                    {renderChatMessage(entry, true)}
                                </Fragment>
                            )}
                        />
                    </Fragment>
                );
            case "day_divider":
                return <DayDivider key={item.key} day={item.day} />;
            case "new_since":
                return <NewSinceDivider key={item.key} />;
            default:
                return exhaustSwitchCase(item);
        }
    };

    // Compact: collapse everything before the current exchange to a breadcrumb;
    // full: stream every entry. The split is pure (presentation.ts).
    const { earlier, recent } = splitTranscriptForCompact(entries);
    const body =
        density === "compact" ? (
            <>
                {earlier.length > 0 ? (
                    <button
                        type="button"
                        className="bilda-chat-transcript__earlier"
                        aria-expanded={showEarlier}
                        onClick={() => setShowEarlier((open) => !open)}
                    >
                        <Plural
                            value={earlier.length}
                            one="# earlier message"
                            other="# earlier messages"
                        />
                        <Icon
                            name={showEarlier ? "chevron-up" : "chevron-down"}
                        />
                    </button>
                ) : null}
                {showEarlier ? earlier.map(renderEntry) : null}
                {recent.map(renderEntry)}
            </>
        ) : (
            projectThread(entries, now, newSinceId).map(renderItem)
        );

    return (
        <ScrollArea
            maxHeight={maxHeight}
            viewportRef={viewportRef}
            className={`bilda-chat-transcript${className ? ` ${className}` : ""}`}
        >
            <div
                className={`bilda-chat-transcript__list${
                    density === "compact"
                        ? " bilda-chat-transcript__list--compact"
                        : ""
                }`}
                aria-label={ariaLabel}
                aria-live="polite"
                role="log"
            >
                {density === "full" && waitingOutOfView > 0 ? (
                    <WaitingPill
                        count={waitingOutOfView}
                        onShow={showWaiting}
                    />
                ) : null}
                {body}
                {/* The trailing live row: shown for the WHOLE turn, a
                    material build included (its card sits above, as the
                    transcript's tail), so it always says what the agent is
                    doing now (D51) and is never silent (G1). Mounted once per
                    turn, so its clock counts from the turn's start. */}
                {drafting !== undefined ? (
                    <div className="bilda-chat-transcript__trailing">
                        <DraftingIndicator phase={drafting} />
                    </div>
                ) : null}
            </div>
        </ScrollArea>
    );
};

// The speaker line that opens an assistant turn: who speaks, and that it is
// AI-made (the AI marker, once per turn).
const AssistantTurnHead = (): ReactNode => {
    const { t } = useLingui();
    return (
        <div className="bilda-chat-transcript__turn-head">
            <span>{t({ message: "Assistant", context: "chat speaker" })}</span>
            <AiMarker variant="tag" />
        </div>
    );
};
